import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Modal } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { getRecordingStartPath } from '@/entities/transcriptor';

import {
  RecordingModalHeader,
  transcriptorRecordingMessages,
} from '@/features/audioRecording';

import type { GalleryItem } from '../../config';
import { TranscriptorStart } from '../TranscriptorStart/TranscriptorStart';

import { ClassicRecordingModal } from './ClassicRecordingModal';
import styles from './TranscriptorRecordingModal.module.scss';

// The template choice of a new recording. A chosen card closes it and opens
// its start page, where the template is set up and recorded.
const V2RecordingModal: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { visible, data } = useAppSelector(
    (state) => state.modal[ModalID.TranscriptorRecording],
  );
  const patientID = data?.patientID ?? '';

  const closeModal = () =>
    dispatch(modalModel.actions.closeModal(ModalID.TranscriptorRecording));

  const openStartPage = (item: GalleryItem) => {
    closeModal();
    navigate(
      getRecordingStartPath({
        patientID,
        seriesId: item.templateSeriesId,
        schemaId: item.schemaId,
      }),
    );
  };

  return (
    <Modal
      dataComponent="transcriptor-recording-modal"
      isOpen={visible}
      onCancel={closeModal}
      headerContent={
        <RecordingModalHeader
          title={formatMessage(transcriptorRecordingMessages.modalTitle)}
          onCollapse={closeModal}
        />
      }
      containerClassName={styles.modalContainer}
      bodyClassName={styles.modalBody}
      shouldRenderCloseIconButton={false}
      shouldRenderCancelButton={false}
      hideFooter
    >
      <TranscriptorStart onSelect={openStartPage} />
    </Modal>
  );
};

export const TranscriptorRecordingModal: FC = () => {
  const v2Enabled = useAppSelector(
    organizationModel.selectors.selectTranscriptorV2Enabled,
  );

  return v2Enabled ? <V2RecordingModal /> : <ClassicRecordingModal />;
};
